/**
 * EM Hub design tokens — derived from the design handoff (colors_and_type.css)
 * but anchored to the app's existing brand orange (#f54b01).
 *
 * T values are CSS var() references (defined in src/styles/theme.css) so every
 * inline-styled component follows the active [data-theme] automatically.
 *
 * RAW keeps the literal light-theme hex values for contexts where var() cannot
 * resolve: SVG presentation attributes (recharts fill/stroke), canvas drawing,
 * and string concatenation like `${color}40` alpha suffixes.
 */
export const RAW = {
  orange: '#f54b01',
  orangeDeep: '#d8430a',
  fg1: '#0a0a0a',
  fg5: '#6a7282',
  fg7: '#99a1af',
  bgMuted: '#f3f4f6',
  border: '#e5e7eb',
  divider: '#f3f4f6',
  success: '#00a63e', success600: '#008236',
  info: '#2b7fff', info600: '#155dfc',
  warn: '#f54900',
  danger: '#e7000b',
  purple: '#6e11b0', purple500: '#8e51ff',
};

export const T = {
  // ── Brand (app orange) ──────────────────────────────────────────────
  orange: 'var(--em-orange)',
  orangeDeep: 'var(--em-orange-deep)',
  orange50: 'var(--em-orange-50)',
  orange100: 'var(--em-orange-100)',
  orangeSoft: 'var(--em-orange-soft)',
  orangeEdge: 'var(--em-orange-edge)',
  gradient: 'var(--em-gradient)',
  glow: 'var(--em-glow)',

  // ── Foreground ──────────────────────────────────────────────────────
  fg1: 'var(--em-fg1)',
  fg2: 'var(--em-fg2)',
  fg3: 'var(--em-fg3)',
  fg4: 'var(--em-fg4)',
  fg5: 'var(--em-fg5)',
  fg6: 'var(--em-fg6)',
  fg7: 'var(--em-fg7)',
  inverse: 'var(--em-inverse)',

  // ── Background ──────────────────────────────────────────────────────
  bgApp: 'var(--em-bg-app)',
  bgCard: 'var(--em-bg-card)',
  bgMuted: 'var(--em-bg-muted)',
  bgTab: 'var(--em-bg-tab)',
  bgHead: 'var(--em-bg-head)',
  bgZebra: 'var(--em-bg-zebra)',
  bgBlack: 'var(--em-bg-black)',

  // ── Borders ─────────────────────────────────────────────────────────
  border: 'var(--em-border)',
  borderSoft: 'var(--em-border-soft)',
  divider: 'var(--em-divider)',

  // ── Semantic ────────────────────────────────────────────────────────
  success: 'var(--em-success)', success600: 'var(--em-success-600)', success50: 'var(--em-success-50)',
  info: 'var(--em-info)', info600: 'var(--em-info-600)', info50: 'var(--em-info-50)',
  warn: 'var(--em-warn)', warn50: 'var(--em-warn-50)',
  danger: 'var(--em-danger)', danger50: 'var(--em-danger-50)',
  purple: 'var(--em-purple)', purple500: 'var(--em-purple-500)', purple50: 'var(--em-purple-50)',

  // ── Radii ───────────────────────────────────────────────────────────
  rXs: 4, rSm: 6, rMd: 8, rLg: 10, rXl: 14, pill: 9999,

  // ── Shadows ─────────────────────────────────────────────────────────
  shadowXs: 'var(--em-shadow-xs)',
  shadowSm: 'var(--em-shadow-sm)',
  shadowMd: 'var(--em-shadow-md)',
  shadowBrand: 'var(--em-shadow-brand)',
  ringFocus: 'var(--em-ring-focus)',

  font: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

// Deterministic avatar color from a name (warm-leaning palette).
const AVATAR_COLORS = ['#f54b01', '#2b7fff', '#00a63e', '#8e51ff', '#f59e0b', '#e7000b', '#0891b2'];
export const avatarColor = (name = '') => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};

export const initialsOf = (name = '') =>
  name.trim().split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase() || '').join('') || '?';
